import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Copy } from "react-feather";
import { ThemeProvider } from "@mui/material/styles";
import muiTheme, { DIALOG_RADIUS } from "../../../theme/mui/styles";

/**
 * MUI implementation of the Vendor Accounts modal.
 * Mirrors the behavior of the Chakra version in vendorsList.js.
 */
export default function VendorAccountsModal({
  isOpen,
  onClose,
  vendor,
  loading,
  data,
  error,
  onOpenCopyModal,
  formatCurrency,
  onRetry,
}) {
  const summary = data?.summary || {
    total_clients_found: 0,
    total_vendors_matched: 0,
    total_journal_entries: 0,
  };

  const hasResults = (data?.clients || []).length > 0;

  const theme = muiTheme;

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={Boolean(isOpen)}
        onClose={onClose}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "96vw",
            maxWidth: "96vw",
            alignSelf: "flex-start",
            mt: 10,
            borderRadius: DIALOG_RADIUS,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            pr: 6,
            display: "flex",
            alignItems: "center",
            fontSize: 18,
            fontWeight: 600,
          }}
        >
          {vendor?.name ? `Accounts for ${vendor.name}` : "Accounts"}
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ ml: "auto" }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, pb: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress color="primary" />
          </Box>
        ) : error ? (
          <Stack spacing={2}>
            <Alert severity="error">
              <AlertTitle>Unable to load accounts</AlertTitle>
              {error}
            </Alert>
            {onRetry && (
              <Button onClick={onRetry} variant="contained">
                Try Again
              </Button>
            )}
          </Stack>
        ) : hasResults ? (
            <Stack spacing={3}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                flexWrap="wrap"
              >
                <Typography variant="h6" fontWeight={600} sx={{ fontSize: 16 }}>
                  Account Results
                </Typography>
                <Stack direction="row" spacing={3} alignItems="center">
                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                    <strong>{summary.total_clients_found}</strong> accounts found
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                    <strong>{summary.total_vendors_matched}</strong> vendors matched
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                    <strong>{summary.total_journal_entries}</strong> transactions
                  </Typography>
                  <IconButton
                    aria-label="Copy account results"
                    size="small"
                    onClick={onOpenCopyModal}
                    disabled={!hasResults}
                    sx={{
                      border: "1px solid #D1D5DB",
                      bgcolor: "#F9FAFB",
                      width: 34,
                      height: 34,
                    }}
                  >
                    <Copy size={16} />
                  </IconButton>
                </Stack>
              </Box>

              <Box
                border={1}
                borderColor="#E5E7EB"
                borderRadius={1}
                overflow="hidden"
              >
                <Table size="small">
                  <TableHead>
                        <TableRow sx={{ backgroundColor: "#E8EEFF" }}>
                      {[
                        "Account Name",
                        "Primary Contact",
                        "Email",
                        "Bookkeeper",
                        "Vendor",
                        "Transactions",
                        "Total Amount",
                      ].map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            color: "#4B5563",
                          }}
                          align={
                            header === "Transactions" || header === "Total Amount"
                              ? "right"
                              : "left"
                          }
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.clients.map((client) => (
                      <TableRow key={`${client.customer_id}-${client.vendor_id}`}>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography fontWeight={600}>
                              {client.customer_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {client.customer_id}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {client.customer_primary_contact || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {client.customer_email || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {client.customer_bookkeeper || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography variant="body2">
                              {client.vendor_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {client.vendor_id}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{ color: "#536DEC", fontWeight: 600 }}
                          >
                            {client.journal_entries_count}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            fontWeight={700}
                            color={
                              Number(client.total_amount || 0) >= 0
                                ? "success.main"
                                : "error.main"
                            }
                          >
                            {formatCurrency(client.total_amount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
        ) : (
          <Alert severity="info">
            <AlertTitle>No Accounts Found</AlertTitle>
            We could not find any accounts for this vendor.
          </Alert>
        )}
      </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
}
